<template>
  <div v-if="loading"><FieldSkeleton/></div>
  <div v-else>
    <RMSFieldLabel
      v-if="config.showLabel ?? true"
      :fieldId="fieldId"
      :fieldLabel="config.label"
      :config="{
        showAvailable: config.showAvailable,
        showRequired: config.showRequired,
        showTooltipIcon: config.showTooltipIcon,
        tooltipConfig: config.tooltipConfig,
      }"
      :optionsCount="options.length"
    />
    <AutoComplete
      :id="fieldId"
      ref="autoCompleteRef"
      v-model="selected"
      :suggestions="filtered"
      :completeOnFocus="true"
      :placeholder="config.placeholder"
      :dataKey="dataKey"
      :optionLabel="config.optionLabel ?? 'name'"
      :panelClass="config.panelClass"
      :virtualScrollerOptions="virtualScrollerOptionsConfig ?? undefined"
      :optionGroupLabel="config.optionGroupLabel ?? undefined"
      :optionGroupChildren="config.optionGroupChildren ?? undefined"
      dropdown
      class="w-full inputfield"
      :disabled="disabled"
      @complete="search"
      @item-select="(event) => $emit('onSelected', event.value)"
      @clear="$emit('onClear')"
    />
  </div>
</template>
<script>
import { onMounted, ref, watch } from 'vue'
import FieldSkeleton from '@/components/fields/FieldSkeleton.vue'
import RMSFieldLabel from '@/components/fields/RMSFieldLabel.vue'
import AutoComplete from 'primevue/autocomplete'

export default {
  name: 'RMSAutoComplete',
  components: {
    FieldSkeleton,
    RMSFieldLabel,
    AutoComplete,
  },
  props: {
    fieldId: {
      type: String,
      default: 'rms-autocomplete'
    },
    config: {
      type: Object,
      default: () => {},
    },
    virtualScrollerOptionsConfig: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Array,
      default: () => [],
    },
    /**
     * @deprecated use originalValue prop instead, which takes into account if the dataKey is something other than 'id'
     */
    originalId: { // Todo: replace all originalId implementations with originalValue
      type: Number,
      default: undefined,
    },
    originalValue: {
      type: Object,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onSelected', 'onAutoSelected', 'onClear'],
  setup(props, { emit }) {
    const autoCompleteRef = ref(null)
    const filtered = ref([])
    const selected = ref(undefined)
    const dataKey = ref(props.config.dataKey ?? 'id')
    // Todo: set this to default to true, but only after review of each "originalValue" context
    const emitAutoSelect = props.config.emitAutoSelect ? !!props.config.emitAutoSelect : false

    const search = (event) => {
      if (!event.query.trim().length) {
        filtered.value = [...props.options]
      } else {
        filtered.value = props.options.filter((option) => {
          return option[autoCompleteRef.value.optionLabel]
            .toString()
            .toLowerCase()
            .includes(event.query.toLowerCase())
        })
      }
    }

    const autoSelect = () => {
      if (props.originalValue) {
        selected.value = props.options.find(option => option[dataKey.value] === props.originalValue[dataKey.value])
      } else {
        // deprecated
        selected.value = props.options.find(option => option[dataKey.value] === props.originalId)
      }
      emitAutoSelect ? emit('onAutoSelected', selected.value) : emit('onSelected', selected.value)
    }

    const resetSelected = () => {
      if (props.originalValue) {
        selected.value = props.options.find(option => option[dataKey.value] === props.originalValue[dataKey.value])
      } else {
        // deprecated
        selected.value = props.options.find(option => option[dataKey.value] === props.originalId)
      }
    }

    onMounted(() => {
      filtered.value = props.options
      if (props.originalValue && props.options.length) {
        autoSelect()
      } else if (props.originalId && props.options.length) {
        // deprecated
        autoSelect()
      }
    })

    watch(
      () => [
        props.originalValue,
        props.originalId, // deprecated
        props.options
      ],
      () => {
        if ((props.originalValue && props.options.length) || (props.originalId && props.options.length)) {
          autoSelect()
        }
      }
    )

    return {
      autoCompleteRef,
      filtered,
      dataKey,
      selected,
      search,
      resetSelected,
    }
  },
}
</script>
